import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Row,
  Col,
  Alert,
  Table,
  Spinner
} from "reactstrap";
import { useDispatch, useSelector } from 'react-redux';
import { generateBatchSettlement } from '../../store/actions';
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import { toast } from 'react-toastify';

const BatchSettlementModal = ({ isOpen, toggleModal, selectedInvoices, serviceProvider }) => {
  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.transactions);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const prevTransactionsRef = useRef(transactions);
  const submitTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isProcessing) {
      prevTransactionsRef.current = transactions;
      return;
    }

    if (prevTransactionsRef.current !== transactions) {
      setIsProcessing(false);
      setIsConfirmationOpen(false);
      toggleModal();
    }

    prevTransactionsRef.current = transactions;
  }, [transactions, isProcessing, toggleModal]);

  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const initialIds = (selectedInvoices || []).map((invoice) => invoice.id).filter(Boolean);
    setSelectedInvoiceIds(initialIds);
  }, [isOpen, selectedInvoices]);

  const selectedInvoicesForBatch = useMemo(() => {
    const idSet = new Set(selectedInvoiceIds);
    return (selectedInvoices || []).filter((invoice) => idSet.has(invoice.id));
  }, [selectedInvoices, selectedInvoiceIds]);

  const allSelected =
    (selectedInvoices || []).length > 0 &&
    selectedInvoicesForBatch.length === (selectedInvoices || []).length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedInvoiceIds([]);
      return;
    }

    setSelectedInvoiceIds((selectedInvoices || []).map((invoice) => invoice.id).filter(Boolean));
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    
    try {
      let parsedDate;
      
      if (typeof date === 'object' && 'seconds' in date) {
        parsedDate = new Date(date.seconds * 1000);
      } else if (typeof date === 'string') {
        parsedDate = parseISO(date);
      } else if (date instanceof Date) {
        parsedDate = date;
      } else {
        return 'تاريخ غير صالح';
      }
      
      return format(parsedDate, 'yyyy/MM/dd', { locale: ar });
    } catch (error) {
      return 'تاريخ غير صالح';
    }
  };

  // Calculate aggregated totals
  const batchSummary = useMemo(() => {
    if (!selectedInvoicesForBatch || selectedInvoicesForBatch.length === 0) {
      return {
        invoiceCount: 0,
        totalGross: 0,
        totalAppCommission: 0,
        totalServiceProviderCommission: 0,
        totalCommission: 0,
        totalCommissionVAT: 0,
        totalFines: 0,
        totalPlatformRevenue: 0,
      };
    }

    const VAT_RATE = 0.15;
    
    let totalGross = 0;
    let totalAppCommission = 0;
    let totalServiceProviderCommission = 0;
    let totalFines = 0;
    let totalCommissionVAT = 0;

    selectedInvoicesForBatch.forEach(invoice => {
      const fineDeductions = Number(invoice.fine_deductions_total || 0);
      const grossAmount = Number(
        invoice.total_after_discount_before_fine ||
        invoice.total_after_discount ||
        0
      );
      
      const serviceVAT = grossAmount * (VAT_RATE / (1 + VAT_RATE));
      const serviceNet = grossAmount - serviceVAT;
      
      const appCommissionRate = Number(invoice.app_commission_rate || 0);
      const serviceProviderCommissionRate = Number(invoice.service_provider_commission_rate || 0);
      
      const appCommission = serviceNet * (appCommissionRate / 100);
      const serviceProviderCommission = serviceNet * (serviceProviderCommissionRate / 100);
      
      totalGross += grossAmount;
      totalAppCommission += appCommission;
      totalServiceProviderCommission += serviceProviderCommission;
      totalFines += fineDeductions;
    });

    const totalCommission = totalAppCommission + totalServiceProviderCommission;
    totalCommissionVAT = totalCommission * VAT_RATE;
    const totalPlatformRevenue = totalCommission + totalCommissionVAT + totalFines;

    return {
      invoiceCount: selectedInvoicesForBatch.length,
      totalGross,
      totalAppCommission,
      totalServiceProviderCommission,
      totalCommission,
      totalCommissionVAT,
      totalFines,
      totalPlatformRevenue,
    };
  }, [selectedInvoicesForBatch]);

  // Get date range
  const dateRange = useMemo(() => {
    if (!selectedInvoicesForBatch || selectedInvoicesForBatch.length === 0) {
      return {
        start: new Date().toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0],
      };
    }

    const dates = selectedInvoicesForBatch
      .map(inv => {
        try {
          if (inv.created_at?.seconds) {
            return new Date(inv.created_at.seconds * 1000);
          } else if (typeof inv.created_at === 'string') {
            return new Date(inv.created_at);
          } else if (inv.created_at instanceof Date) {
            return inv.created_at;
          }
        } catch (e) {
          console.error('Error parsing date:', inv.created_at);
        }
        return null;
      })
      .filter(d => d && !isNaN(d.getTime()))
      .sort((a, b) => a - b);

    if (dates.length === 0) {
      const today = new Date();
      return {
        start: format(today, 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      };
    }

    return {
      start: format(dates[0], 'yyyy-MM-dd'),
      end: format(dates[dates.length - 1], 'yyyy-MM-dd'),
    };
  }, [selectedInvoicesForBatch]);

  const handleConfirm = () => {
    if (!selectedInvoicesForBatch.length) {
      toast.error("يرجى اختيار فاتورة واحدة على الأقل لإنشاء التسوية المجمعة.");
      return;
    }

    const paidValues = ["paid", "completed", "success", "succeeded"];

    const isRefundMarked = (invoice) => {
      const refundStatuses = ["pending", "approved", "completed", "refunded"];
      return (
        invoice?.is_refund_settlement === true ||
        invoice?.settlement_type === "refund" ||
        invoice?.needs_refund === true ||
        refundStatuses.includes(String(invoice?.refund_status || "").toLowerCase()) ||
        Boolean(invoice?.refund_request_id)
      );
    };

    const invalidInvoices = selectedInvoicesForBatch.filter((invoice) => {
      const bookingStatus = String(invoice?.booking?.booking_status || "").toLowerCase();
      const paymentStatus = String(
        invoice?.booking?.paymentStatus || invoice?.booking?.payment_status || ""
      ).toLowerCase();

      const isCompletedAndPaid = bookingStatus === "completed" && paidValues.includes(paymentStatus);
      return !isCompletedAndPaid || isRefundMarked(invoice);
    });

    if (invalidInvoices.length > 0) {
      toast.error(
        `لا يمكن إنشاء التسوية المجمعة: يوجد ${invalidInvoices.length} فاتورة غير مؤهلة (يجب أن تكون الطلبات مكتملة ومدفوعة وغير معلمة كاسترجاع).`
      );
      return;
    }

    setIsConfirmationOpen(true);
  };

  const handleProceed = () => {
    if (!selectedInvoicesForBatch.length) {
      toast.error("لا توجد فواتير محددة للتسوية.");
      return;
    }

    if (isProcessing) return;

    setIsProcessing(true);
    dispatch(
      generateBatchSettlement(
        serviceProvider.id,
        dateRange.start,
        dateRange.end,
        selectedInvoicesForBatch
      )
    );

    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }

    submitTimeoutRef.current = setTimeout(() => {
      setIsProcessing(false);
      toast.warning("ما زالت عملية إنشاء التسوية قيد التنفيذ... يرجى الانتظار.");
    }, 25000);
  };

  const handleCancel = () => {
    setIsConfirmationOpen(false);
  };

  // Return null if no data
  if (!selectedInvoices || selectedInvoices.length === 0 || !serviceProvider) {
    return null;
  }

  return (
    <>
      {/* Main Batch Settlement Modal */}
      <Modal isOpen={isOpen} toggle={toggleModal} size="xl" centered>
        <ModalHeader toggle={toggleModal} className="border-0 pb-0">
          <div>
            <h4 className="modal-title">إنشاء تسوية مجمعة</h4>
            <small className="text-muted">
              مقدم الخدمة: <strong>{serviceProvider?.salon_name}</strong> | 
              الفترة: {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
            </small>
          </div>
        </ModalHeader>
        
        <ModalBody>
          <Row>
            <Col md={5}>
              <Card className="mb-3 border-primary">
                <CardBody>
                  <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                    <i className="bx bx-receipt me-2"></i>
                    ملخص التسوية المجمعة
                  </CardTitle>
                  
                  <div className="summary-section">
                    <div className="alert alert-info mb-3">
                      <div className="d-flex justify-content-between">
                        <span><i className="bx bx-file me-1"></i>عدد الفواتير:</span>
                        <strong>{batchSummary.invoiceCount}</strong>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">إجمالي قيمة الخدمات:</span>
                      <span>{batchSummary.totalGross.toFixed(2)} ريال</span>
                    </div>

                    <hr className="my-3" />

                    <div className="alert alert-light p-2 mb-3">
                      <small className="text-muted d-block mb-2">العمولات المجمعة:</small>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="small">عمولة التطبيق:</span>
                        <span className="text-danger small">{batchSummary.totalAppCommission.toFixed(2)} ريال</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="small">عمولة مقدم الخدمة:</span>
                        <span className="text-danger small">{batchSummary.totalServiceProviderCommission.toFixed(2)} ريال</span>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">إجمالي العمولات:</span>
                      <span className="text-danger">{batchSummary.totalCommission.toFixed(2)} ريال</span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">ضريبة العمولات (15%):</span>
                      <span className="text-danger">{batchSummary.totalCommissionVAT.toFixed(2)} ريال</span>
                    </div>

                    {batchSummary.totalFines > 0 && (
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">غرامات الإلغاء:</span>
                        <span className="text-warning">{batchSummary.totalFines.toFixed(2)} ريال</span>
                      </div>
                    )}

                    <hr className="my-3" />

                    <div className="alert alert-success p-3 mb-0">
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold text-success">إجمالي إيراد المنصة:</span>
                        <span className="fw-bold text-success fs-5">
                          {batchSummary.totalPlatformRevenue.toFixed(2)} ريال
                        </span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={7}>
              <Card className="mb-3">
                <CardBody>
                  <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                    <i className="bx bx-list-ul me-2"></i>
                    الفواتير المختارة ({batchSummary.invoiceCount} من {selectedInvoices.length})
                  </CardTitle>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">اختر الفواتير المطلوب تسويتها لهذا الصالون فقط</small>
                    <Button color="light" size="sm" onClick={toggleSelectAll}>
                      {allSelected ? "إلغاء تحديد الكل" : "تحديد الكل"}
                    </Button>
                  </div>
                  
                  <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table size="sm" striped hover>
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="text-center">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                              aria-label="select-all-invoices"
                            />
                          </th>
                          <th>#</th>
                          <th>التاريخ</th>
                          <th>الخدمة</th>
                          <th>العميل</th>
                          <th>المبلغ</th>
                          <th>غرامة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoices.map((invoice, idx) => (
                          <tr key={invoice.id}>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={selectedInvoiceIds.includes(invoice.id)}
                                onChange={() => toggleInvoiceSelection(invoice.id)}
                                aria-label={`select-invoice-${invoice.id}`}
                              />
                            </td>
                            <td>{idx + 1}</td>
                            <td className="small">{formatDate(invoice.created_at)}</td>
                            <td className="small">{invoice.serviceProviderServices?.name || 'N/A'}</td>
                            <td className="small">{invoice.app_user_id?.username || 'N/A'}</td>
                            <td className="small">
                              {Number(invoice.total_after_discount || 0).toFixed(2)}
                            </td>
                            <td className="small">
                              {Number(invoice.fine_deductions_total || 0) > 0 ? (
                                <Badge color="warning" className="p-1">
                                  {Number(invoice.fine_deductions_total).toFixed(2)}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Alert color="info" className="mt-3">
            <i className="bx bx-info-circle me-2"></i>
            سيتم إنشاء <strong>فاتورة ERP واحدة</strong> تحتوي على جميع العمولات والغرامات للفواتير المدرجة أعلاه.
            <br />
            <small className="text-muted mt-1 d-block">
              ملاحظة: سيتم إنشاء بيانات تسوية فردية لكل عميل مع الاحتفاظ بفاتورة ERP واحدة مجمعة.
            </small>
          </Alert>
        </ModalBody>

        <ModalFooter className="border-0">
          <Button color="secondary" onClick={toggleModal} className="me-2">
            <i className="bx bx-x me-1"></i>
            إلغاء
          </Button>
          <Button color="success" onClick={handleConfirm} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-1" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <i className="bx bx-check-circle me-1"></i>
                إنشاء التسوية المجمعة
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmationOpen} toggle={handleCancel} centered>
        <ModalHeader toggle={handleCancel}>تأكيد إنشاء التسوية المجمعة</ModalHeader>
        <ModalBody>
          <div className="text-center py-3">
            <i className="bx bx-check-circle display-4 text-success mb-3"></i>
            <h5>هل أنت متأكد من إنشاء التسوية المجمعة؟</h5>
            <p className="text-muted mt-2">
              سيتم إنشاء فاتورة ERP واحدة لـ <strong>{batchSummary.invoiceCount}</strong> فاتورة محددة
            </p>
            
            <Card className="bg-light mt-3">
              <CardBody>
                <Row>
                  <Col md={4} className="text-center border-end">
                    <small className="text-muted">عدد الفواتير</small>
                    <p className="fw-bold mb-0">{batchSummary.invoiceCount}</p>
                  </Col>
                  <Col md={4} className="text-center border-end">
                    <small className="text-muted">الفترة</small>
                    <p className="fw-bold mb-0 small">
                      {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
                    </p>
                  </Col>
                  <Col md={4} className="text-center">
                    <small className="text-muted">إجمالي الإيراد</small>
                    <p className="fw-bold text-success mb-0">
                      {batchSummary.totalPlatformRevenue.toFixed(2)}
                    </p>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Alert color="warning" className="mt-3 text-start">
              <small>
                <i className="bx bx-info-circle me-1"></i>
                لا يمكن التراجع عن هذا الإجراء بعد التنفيذ
              </small>
            </Alert>

            {isProcessing && (
              <Alert color="info" className="mt-3 text-start">
                <Spinner size="sm" className="me-2" />
                جاري إنشاء التسوية المجمعة... يرجى عدم الإغلاق أو الضغط المتكرر.
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-center border-0">
          <Button color="secondary" onClick={handleCancel} className="px-4" disabled={isProcessing}>
            <i className="bx bx-x me-1"></i>
            إلغاء
          </Button>
          <Button color="success" onClick={handleProceed} className="px-4 me-2">
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-1" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <i className="bx bx-check me-1"></i>
                نعم، أنشئ التسوية
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default BatchSettlementModal;
